import { PokeNames } from "types/pokemon/poke-names";
import { PokeType } from "types/pokemon/poke-type";

export enum RoomDifficulty {
  EASY = "easy",
  NORMAL = "normal",
  HARD = "hard",
}

// ============================================================================
// Room Modifiers
// ============================================================================

export enum RoomModifierType {
  /** Weather effects that boost/nerf certain types */
  WEATHER_RAIN = "weather_rain",
  WEATHER_SUN = "weather_sun",
  WEATHER_SANDSTORM = "weather_sandstorm",
  WEATHER_HAIL = "weather_hail",

  /** Enemies have increased stats */
  STAT_BOOST_ATK = "stat_boost_atk",
  STAT_BOOST_DEF = "stat_boost_def",
  STAT_BOOST_SPD = "stat_boost_spd",

  /** Super-effective moves deal extra damage */
  TYPE_ADVANTAGE_BOOST = "type_advantage_boost",

  /** Pokemon cannot be healed during battle */
  HEALING_BLOCK = "healing_block",

  /** Overtime triggers faster */
  TIME_PRESSURE = "time_pressure",

  /** All attacks deal 2x damage (both sides) */
  DOUBLE_DAMAGE = "double_damage",
}

export interface RoomModifier {
  type: RoomModifierType;
  /** Modifier-specific value (e.g., stat boost percentage) */
  value?: number;
}

// ============================================================================
// Room Rewards
// ============================================================================

export enum RoomRewardType {
  /** Adds one random enemy Pokemon from this room to recruitment pool */
  RECRUIT_POKEMON = "recruit_pokemon",
  /** Guaranteed item drop */
  ITEM = "item",
  /** Full heal for all Pokemon */
  FULL_HEAL = "full_heal",
  /** Revive one fainted Pokemon */
  REVIVE = "revive",
  /** Reduces boss stats and increases rare/epic boss chance */
  BOSS_DISCOVERY = "boss_discovery",
}

export interface RoomExtraReward {
  type: RoomRewardType;
  /** Reward-specific value (e.g., item name, pokemon name) */
  value?: string;
}

// ============================================================================
// Room Configuration
// ============================================================================

export interface RaidRoomEnemy {
  name: PokeNames;
  level: number;
}

export interface BaseRaidRoomConfig {
  /** Unique ID for this room option (0-3 during voting) */
  id: number;

  /** Primary type - always the raid type */
  mainType: PokeType;

  /** Optional secondary type */
  secondaryType?: PokeType;

  /** Optional room modifier */
  modifier?: RoomModifier;

  /** Optional extra reward */
  extraReward?: RoomExtraReward;

  /** Map asset ID for visual rendering */
  mapId: string;
}

export interface RaidRoomConfig extends BaseRaidRoomConfig {
  kind: "room";

  /** Room difficulty level */
  difficulty: RoomDifficulty;

  /** Enemy Pokemon in this room */
  enemies: RaidRoomEnemy[];
}

// ============================================================================
// Boss Room Configuration
// ============================================================================

export enum BossTier {
  /** "Big" version of normal Pokemon */
  MINI_BOSS = "mini_boss",
  /** Stronger Pokemon, may have minions */
  BOSS = "boss",
  /** Regional variants or Mega Pokemon */
  EPIC_BOSS = "epic_boss",
}

export interface RaidBossConfig extends BaseRaidRoomConfig {
  kind: "boss";

  /** Boss tier determines rewards and difficulty */
  tier: BossTier;

  /** The main boss Pokemon */
  boss: RaidRoomEnemy;

  /** Optional minion Pokemon that spawn with the boss */
  minions?: RaidRoomEnemy[];

  /** Whether this is a Mega Pokemon boss */
  isMega?: boolean;

  /** Whether this is a regional variant (Alola/Galar/Hisui) */
  regionalVariant?: "alola" | "galar" | "hisui";
}

export type RaidRoomOption = RaidRoomConfig | RaidBossConfig;
